<template>
  <div class="team-inbox-maintenance">
    <div class="text-left mb-2">
      <div class="form-label">
        <h5>Team Inbox Maintenance</h5>
      </div>
    </div>
    <div class="card">
      <div class="card-body">
        <el-button type="danger" 
                   size="small"
                   @click="showConfirmationModal">
          Clear All Unread Items
        </el-button>
        <p class="text-muted mt-2 mb-0">
          <small>This will permanently mark all unread calls, messages, and voicemails in this Team Inbox as read. This action cannot be undone.</small>
        </p>
      </div>
    </div>
    
    <el-dialog
      :title="'⚠️ Confirm: Mark All Unread Items as Read?'"
      :visible.sync="confirmationModalVisible"
      width="500px"
      :close-on-press-escape="!markingAsRead"
      :show-close="!markingAsRead"
      :custom-class="'team-inbox-confirm-dialog'">
      <form @submit.prevent="markAllAsRead">
        <div v-if="loadingUnreadCount || unreadCount !== null" class="unread-count-display">
          <span v-if="loadingUnreadCount">
            <i class="el-icon-loading"></i> Loading...
          </span>
          <span v-else>
            {{ unreadCount.toLocaleString() }} Total Unread
          </span>
        </div>
        <p>
          You are about to perform an irreversible action on the <strong>'{{ ringGroup.name }}'</strong> Team Inbox. 
          To proceed, please type the name of the inbox below to confirm.
        </p>
        <el-input 
          v-model="confirmationName" 
          placeholder="Type the Team Inbox name"
          :disabled="markingAsRead">
        </el-input>
      </form>
      <span slot="footer" class="dialog-footer">
        <el-button 
          type="danger" 
          :disabled="confirmationName !== ringGroup.name || markingAsRead"
          :loading="markingAsRead"
          @click="markAllAsRead">
          I understand, Mark All as Read
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import auth from "../../auth"
import { acl_mixin } from "../../mixins"

export default {
  name: "team-inbox-maintenance",
  mixins: [acl_mixin],

  props: {
    ringGroup: {
      required: true,
      type: Object,
    },
  },

  data() {
    return {
      auth,
      confirmationModalVisible: false,
      confirmationName: '',
      markingAsRead: false,
      unreadCount: null,
      loadingUnreadCount: false
    }
  },

  methods: {
    showConfirmationModal() {
      // Show modal immediately
      this.confirmationModalVisible = true
      this.confirmationName = ''
      this.loadingUnreadCount = true
      this.unreadCount = null
      
      // Fetch count in background
      this.fetchUnreadCount()
    },
    
    async fetchUnreadCount() {
      try {
        // Fetch unread count using existing v2 endpoint
        const response = await axios.post('/api/v2/inboxes/unread-count', {
          inbox_ids: [this.ringGroup.id]
        })
        
        if (response.data && response.data.length > 0) {
          this.unreadCount = response.data[0].unread_count || 0
        } else {
          this.unreadCount = 0
        }
      } catch (error) {
        console.error('Failed to fetch unread count:', error)
        // Continue without showing count
      } finally {
        this.loadingUnreadCount = false
      }
    },

    markAllAsRead() {
      if (this.confirmationName !== this.ringGroup.name) {
        this.$message.error('Please enter the exact Team Inbox name to confirm')
        return
      }

      this.markingAsRead = true

      axios.post(`/api/v1/ring-group/${this.ringGroup.id}/mark-all-unread-as-read`)
      .then(response => {
        this.markingAsRead = false
        this.confirmationModalVisible = false
        this.confirmationName = ''
        this.unreadCount = null
        
        this.$message({
          type: 'success',
          message: response.data.message || 'Processing has started. All unread items will be marked as read shortly.',
          duration: 5000
        })
      })
      .catch(error => {
        this.markingAsRead = false
        this.$root.handleErrors(error.response)
      })
    },
  }
}
</script>

<style lang="scss" scoped>
.team-inbox-maintenance {
  .card {
    border: 1px solid #e4e7ed;
    border-radius: 4px;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.06);
    
    .card-body {
      padding: 20px;
    }
  }
  
  .form-label {
    h5 {
      margin: 0;
      font-size: 18px;
      font-weight: 500;
      color: #303133;
    }
  }
  
  .unread-count-display {
    text-align: center;
    margin: 0 0 15px 0;
    font-size: 20px;
    font-weight: bold;
    color: #f56c6c;
    
    @media (max-width: 768px) {
      font-size: 18px;
      margin: 0 0 12px 0;
    }
  }
  
  .dialog-footer {
    display: flex;
    justify-content: center;
  }
}

// Global styles for the dialog
::v-deep .el-dialog__wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
}

::v-deep .team-inbox-confirm-dialog {
  margin: 0 !important;
  
  @media (max-width: 768px) {
    width: 90% !important;
  }
  
  .el-dialog__body {
    padding: 0 20px 15px;
    
    @media (max-width: 768px) {
      padding: 0 15px 10px;
    }
  }
  
  .el-dialog__header {
    padding: 20px 50px;
    position: relative;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 60px;
    
    @media (max-width: 768px) {
      padding: 15px 45px;
      min-height: 50px;
    }
    
    .el-dialog__title {
      display: inline-block;
      max-width: 100%;
      word-wrap: break-word;
      white-space: normal;
      margin: 0;
      line-height: 1.4;
    }
    
    .el-dialog__headerbtn {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      right: 20px;
      
      @media (max-width: 768px) {
        right: 15px;
      }
    }
  }
  
  .el-dialog__footer {
    padding: 10px 20px 15px;
    
    @media (max-width: 768px) {
      padding: 8px 15px 12px;
    }
  }
}
</style>